import parseHtml from "html-react-parser";
import { useRouter } from "next/router";
import Head from "next/head";
import React from "react";
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from "next";

import useWebflowScripts from "@/utils/hooks/webflow/useWebflowScripts";
import { parseHEADOptions, parseOptions } from "@/utils/webflow/client";
import { getWebflowPage, WebflowProps } from "@/utils/webflow/server";
import useCaptureDynamicPageView from "@/utils/analytics/captureDynamicPageView";
import LoggedOutOnly from "@/components/LoggedOutOnly";
import RegisterCTAs from "@/components/RegisterCTAs";

export default function BlogPost(props: WebflowProps & { slug: string }) {
  useWebflowScripts(props);
  const router = useRouter();

  useCaptureDynamicPageView("/blog/post/[slug]", { slug: props.slug });

  if (!props.bodyContent) return null;

  return (
    <>
      <Head>
        <link rel="preload" href="/styles/wf-default.css" as="style" />
        {props.headContent && parseHtml(props.headContent, parseHEADOptions)}
        {/* eslint-disable-next-line @next/next/no-css-tags */}
        <link rel="stylesheet" href="/styles/wf-default.css" />
        <link rel="canonical" href={`https://pianity.com${router.asPath.split("?")[0]}`} key="canonical" />
      </Head>
      <div className="wf-default">{parseHtml(props.bodyContent, parseOptions)}</div>
      <LoggedOutOnly>
        <RegisterCTAs />
      </LoggedOutOnly>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps(
  ctx: GetStaticPropsContext
): Promise<GetStaticPropsResult<WebflowProps & { slug: string }>> {
  const { slug } = ctx.params ?? {};
  if (!slug || typeof slug !== "string") {
    return { notFound: true, revalidate: 60 * 5 };
  }

  const baseUrl = process.env.WEBFLOW_URI;
  const fetchUrl = `${baseUrl}/post/${slug}`;

  return await getWebflowPage(fetchUrl, { slug }, ctx.locale);
}
